import { IChatsAPIErrorResponse } from '@cian/chats-api/lib/browser-entry';

import { IAlertState } from './alert';
import { TChatActionModalState } from './chatActionModal';
import { TChatCustomActionModalState } from './chatCustomActionModal';
import { TChatListState } from './chatList';
import { TChatWindowState } from './chatWindow';
import { IContentFullscreenState } from './contentFullscreen';
import { TExperimentsState } from './experiments';
import { EHostType } from './hostType';
import { TLkType } from './lkType';
import { EMasterTabs, TMasterFiltersState } from './masterFilters';
import { EMobileLayoutActiveSide, TMobileLayoutActiveSideState } from './mobileSide';
import { IOffersSelector } from './offersForSelection';
import { TOpenedChats } from './openedChats';
import { EPageType } from './pageType';
import { ERoute } from './route';
import { ISessionInitialState, TSessionState } from './session';
import { TSocketState } from './socket';
import { TSubAgentsState } from './subAgent';
import { IUserCardState } from './userCard';
import { TUser } from './userData';
import { TUsersStatusState } from './usersStatus';
import { EInternetStatus, TInternetStatus } from '../actions/internetStatus';

export type TAPIErrors = IChatsAPIErrorResponse;

export enum ERequestApiStatus {
  Initial = 'initial',
  Pending = 'pending',
  Succeed = 'succeed',
  Failed = 'failed',
}

export enum EWsClientEventType {
  Message = 'message',
  Session = 'session',
  Answer = 'answer',
  UnsubscribeUsersStatus = 'unsubscribeUsersStatus',
  SubscribeUsersStatus = 'subscribeUsersStatus',
}

export type TSerializableAppState = {
  alert: IAlertState;
  chatList: TChatListState;
  internetStatus: TInternetStatus;
  mobileSide: TMobileLayoutActiveSideState;
  chatWindow: TChatWindowState;
  openedChats: TOpenedChats;
  session: TSessionState;
  showErrorPage?: boolean;
  user: TUser;
  lkType: TLkType;
  subAgents: TSubAgentsState;
  hostType: EHostType;
  pageType: EPageType;
  contentFullscreen: IContentFullscreenState;
  chatActionModal: TChatActionModalState;
  chatCustomActionModal: TChatCustomActionModalState;
  usersStatus: TUsersStatusState;
  experiments: TExperimentsState;
  userCard: IUserCardState;
  offersForSelection: IOffersSelector;
  masterFilters: TMasterFiltersState;
  unreadMessagesCount: number;
};

export type TAppState = TSerializableAppState & {
  socket: TSocketState;
};

export function getInitialState(): Partial<TSerializableAppState> {
  return {
    alert: {
      isVisible: false,
    },
    chatList: {
      chats: [],
      currentPage: 0,
      currentRoute: ERoute.Messages,
      movedToArchiveChatIds: [],
      movedToSpamChatIds: [],
      status: ERequestApiStatus.Initial,
      isChangeListActive: false,
      allChatsLoaded: false,
      selectedChats: [],
    },
    internetStatus: EInternetStatus.Online,
    mobileSide: EMobileLayoutActiveSide.Left,
    chatWindow: {
      currentOpenedChatId: null,
      isChatOperationPending: false,
      isBubbleInputFocused: false,
      messagesMap: new Map(),
    },
    openedChats: {},
    session: {
      status: ERequestApiStatus.Initial,
    } as ISessionInitialState,
    subAgents: {
      subAgentsList: [],
      page: { canLoadMore: true, offset: 0, limit: 0 },
      status: ERequestApiStatus.Initial,
    },
    hostType: EHostType.Page,
    contentFullscreen: {
      isOpen: false,
      selectedImageUid: null,
    },
    chatActionModal: {
      isOpened: false,
    },
    chatCustomActionModal: {
      isOpened: false,
    },
    experiments: [],
    userCard: {
      isOpen: false,
    },
    masterFilters: {
      activeTab: EMasterTabs.MasterChats,
      selectedSubAgentId: undefined,
    },
    unreadMessagesCount: 0,
    showErrorPage: false,
    offersForSelection: {
      page: 0,
      totalCount: 0,
      list: [],
      canLoadMore: true,
      status: ERequestApiStatus.Initial,
    },
  };
}
